import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, Segment, Form, Button } from 'semantic-ui-react';
import { identity as ValidateIdentity } from '@mhub/web-validations';
import CloseSvg from '../../assets/icons/close.svg';
import HLBCampaignMainBanner from './HLBCampaignMainBanner';
import HLBCampaignTncModal from './HLBCampaignTncModal';
import { flashErrorMessage, flashSuccessMessage } from '../../actions/flash';
import { fetchM } from '../../actions/fetchM';
import config from '../../../config';
import BANKS_BY_COUNTRY from '../../constants/banks';
import { REGEX_FULL_NAME, REGEX_BANK_ACCOUNT_NUMBER } from '../../constants/regex';

class HLBCampaignJoinModal extends Component {
  static propTypes = {
    show: PropTypes.bool,
    onClose: PropTypes.func,
    auth: PropTypes.shape({
      id: PropTypes.number,
      email: PropTypes.string,
      preferredName: PropTypes.string,
      affiliateID: PropTypes.number,
      company: PropTypes.shape({
        countryCode: PropTypes.string,
      }),
    }),
  }
  static contextTypes = {
    store: PropTypes.object,
  }
  static mapStateToProps = state => ({
    auth: state.auth,
  })
  constructor(props) {
    super(props);
    this.state = {
      show: false,
      agreed: false,
      fullName: '',
      identityNo: '',
      bankAccountName: '',
      bankList: [],
      bankAccountNumber: '',
      editMode: false,
      errors: {},
      openTncModal: false,
      isSubmitting: false,
      token: null,
      originalValues: {
        fullName: '',
        identityNo: '',
        bankAccountName: '',
        bankAccountNumber: '',
      },
      hasChanges: false,
    };
  }
  componentWillReceiveProps(nextProps) {
    const { show } = nextProps;
    const wasShown = this.props.show;

    this.setState({ show });
    if (show && !wasShown) {
      this.fetchUserDetailsAndAutofill();
    }
  }

  getBankOptions = () => {
    const { auth } = this.props;
    const countryCode = (auth && auth.company && auth.company.countryCode && auth.company.countryCode.toLowerCase()) || 'my';
    const banksByCountry = BANKS_BY_COUNTRY.find(b => b.country === countryCode);

    if (banksByCountry) {
      return {
        data: banksByCountry.value.map(bank => ({
          key: bank.value,
          value: bank.value,
          text: bank.text,
        })),
      };
    }
    const myBanks = BANKS_BY_COUNTRY.find(b => b.country === 'my');
    return {
      data: myBanks ? myBanks.value.map(bank => ({
        key: bank.value,
        value: bank.value,
        text: bank.text,
      })) : [],
    };
  };

  fetchUserDetailsAndAutofill = async () => {
    const { auth } = this.props;
    const { store } = this.context;

    try {
      const res = await store.dispatch(fetchM(`${config.get('bookApiURL')}/staffs/${auth.affiliateID}/`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));

      if (res.ok) {
        const userData = await res.json();
        const token = res.headers.get('X-Csrf-Token');

        const fullName = userData.fullName || '';
        const identityNo = userData.identityNo || '';
        const bankAccountName = userData.bankAccountName || '';
        const bankAccountNumber = userData.bankAccountNumber || '';

        const hasAllFields = fullName && identityNo && bankAccountName && bankAccountNumber;

        this.setState({
          fullName,
          identityNo,
          bankAccountName,
          bankAccountNumber,
          bankList: this.getBankOptions(),
          editMode: hasAllFields,
          token,
          originalValues: {
            fullName,
            identityNo,
            bankAccountName,
            bankAccountNumber,
          },
        }, () => {
          this.setState({ hasChanges: this.hasFormChanges() });
        });
      }
    } catch (error) {
      this.setState({
        fullName: '',
        bankList: this.getBankOptions(),
        editMode: false,
        originalValues: {
          fullName: '',
          identityNo: '',
          bankAccountName: '',
          bankAccountNumber: '',
        },
      }, () => {
        this.setState({ hasChanges: this.hasFormChanges() });
      });
    }
  };

  hasFormChanges = () => {
    const { fullName, identityNo, bankAccountName, bankAccountNumber, originalValues, editMode } = this.state;

    const normalizeValue = value => value || '';

    if (editMode) {
      const fullNameChanged = normalizeValue(fullName) !== normalizeValue(originalValues.fullName);
      const identityNoChanged = normalizeValue(identityNo) !== normalizeValue(originalValues.identityNo);
      const bankAccountNameChanged = normalizeValue(bankAccountName) !== normalizeValue(originalValues.bankAccountName);
      const bankAccountNumberChanged = normalizeValue(bankAccountNumber) !== normalizeValue(originalValues.bankAccountNumber);

      return fullNameChanged || identityNoChanged || bankAccountNameChanged || bankAccountNumberChanged;
    }

    return !!(
      normalizeValue(fullName).trim() ||
      normalizeValue(identityNo).trim() ||
      normalizeValue(bankAccountName) ||
      normalizeValue(bankAccountNumber).trim()
    );
  };

  validate = () => {
    const errors = {};
    const { fullName, identityNo, bankAccountName, bankAccountNumber, agreed } = this.state;

    const hasBankDetails = bankAccountName || (bankAccountNumber && bankAccountNumber.trim());

    if (hasBankDetails) {
      if (!fullName || !fullName.trim()) {
        errors.fullName = 'Full Name is required';
      } else {
        const fullNameRegex = REGEX_FULL_NAME;
        if (!fullNameRegex.test(fullName.trim())) {
          errors.fullName = 'Full Name does not match the format';
        }
      }
    } else if (fullName && fullName.trim()) {
      const fullNameRegex = REGEX_FULL_NAME;
      if (!fullNameRegex.test(fullName.trim())) {
        errors.fullName = 'Full Name does not match the format';
      }
    }
    if (!identityNo || !identityNo.trim()) {
      errors.identityNo = 'NRIC is required';
    } else {
      try {
        const identityValidation = ValidateIdentity(identityNo.trim(), {
          countryCode: 'MY',
          identityType: 'ic_number',
        });
        if (!identityValidation.isValid) {
          errors.identityNo = 'Invalid NRIC format';
        }
      } catch (error) {
        errors.identityNo = 'Invalid NRIC format';
      }
    }
    if (bankAccountNumber && bankAccountNumber.trim()) {
      if (!bankAccountName) {
        errors.bankAccountName = 'Bank is required';
      }
    }
    if (bankAccountName) {
      if (!bankAccountNumber || !bankAccountNumber.trim()) {
        errors.bankAccountNumber = 'Bank account number is required';
      } else {
        const bankAccountRegex = REGEX_BANK_ACCOUNT_NUMBER;
        if (!bankAccountRegex.test(bankAccountNumber.trim())) {
          errors.bankAccountNumber = 'Bank Account Number does not match the format';
        }
      }
    }
    if (!agreed) {
      errors.agreed = 'You must agree to the terms';
    }

    return errors;
  };
  handleOnClose = () => {
    this.setState({
      show: false,
      fullName: '',
      identityNo: '',
      bankAccountName: '',
      bankAccountNumber: '',
      agreed: false,
      errors: {},
      editMode: false,
      isSubmitting: false,
      token: null,
      originalValues: {
        fullName: '',
        identityNo: '',
        bankAccountName: '',
        bankAccountNumber: '',
      },
      hasChanges: false,
    });

    if (this.props.onClose) {
      this.props.onClose();
    }
  }
  handleOnCloseTnc = () => {
    this.setState({ openTncModal: false });
  }
  handleOnOpenTnc = () => {
    this.setState({ openTncModal: true });
  }
  handleInputChange = (e, { name, value }) => {
    this.setState({ [name]: value, errors: { ...this.state.errors, [name]: null } }, () => {
      this.setState({ hasChanges: this.hasFormChanges() });
    });
  }
  handleSubmit = async () => {
    const { fullName, identityNo, bankAccountName, bankAccountNumber, editMode } = this.state;
    const { store } = this.context;
    const { auth } = this.props;

    const errors = this.validate();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      const errorMessages = Object.keys(errors).map(key => errors[key]);

      if (errorMessages.length === 1) {
        await store.dispatch(flashErrorMessage({
          message: errorMessages[0],
        }));
        return;
      }
      await store.dispatch(flashErrorMessage({
        list: errorMessages,
      }));
      return;
    }
    this.setState({ isSubmitting: true });
    try {
      const submissionData = {
        fullName,
        identityNo,
        bankAccountName,
        bankAccountNumber,
      };
      const apiEndpoint = `${config.get('bookApiURL')}/staffs/${auth.affiliateID}/`;
      const res = await store.dispatch(fetchM(apiEndpoint, {
        credentials: 'include',
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': this.state.token,
        },
        body: JSON.stringify(submissionData),
      }));
      if (res.ok) {
        await res.json();
        await store.dispatch(flashSuccessMessage({
          message: editMode
            ? 'Bank details updated successfully'
            : 'Bank details submitted successfully',
        }));
        this.handleOnClose();
      } else {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to submit bank details');
      }
    } catch (error) {
      await store.dispatch(flashErrorMessage({
        message: error.message || 'An error occurred while submitting your bank details. Please try again.',
      }));
    } finally {
      this.setState({ isSubmitting: false });
    }
  };
  render() {
    const { show, agreed, bankList, fullName, identityNo, bankAccountName, bankAccountNumber, editMode, errors, isSubmitting, hasChanges } = this.state;

    return (
      <Modal size="small" open={show} onClose={this.handleOnClose}>
        <Modal.Header>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '18px', fontWeight: '600' }}>Confirm Your Participation</span>
            <button
              type="button"
              onClick={this.handleOnClose}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
              aria-label="Close"
            >
              <img
                src={CloseSvg}
                width={20}
                alt="close"
              />
            </button>
          </div>
        </Modal.Header>
        <Modal.Content className="hlb-campaign-welcome-modal-content">
          <Modal.Description>
            <HLBCampaignMainBanner />
          </Modal.Description>
          <Modal.Description style={{ marginTop: '2em' }}>
            <Segment>
              <p style={{ fontSize: '16px', fontWeight: '600' }}>Register bank account for rewards</p>
              <Form className="hlb-campaign-form" id="hlb-campaign-join-form" onSubmit={this.handleSubmit}>
                <Form.Field error={!!(errors.fullName)}>
                  <label htmlFor="fullName" style={{ marginBottom: '0.5em', fontWeight: '600' }}>
                    Full name (as in MyKad)
                  </label>
                  <Form.Input
                    name="fullName"
                    placeholder="Full name"
                    value={fullName}
                    onChange={this.handleInputChange}
                    error={errors.fullName && <div className="hlb-form-error-message">{errors.fullName}</div>}
                  />
                </Form.Field>
                <Form.Field error={!!(errors.identityNo)}>
                  <label htmlFor="nric" style={{ marginBottom: '0.5em', fontWeight: '600' }}>
                    MyKad (NRIC)
                  </label>
                  <Form.Input
                    name="identityNo"
                    placeholder="MyKad (NRIC)"
                    value={identityNo}
                    onChange={this.handleInputChange}
                    error={errors.identityNo && <div className="hlb-form-error-message">{errors.identityNo}</div>}
                  />
                </Form.Field>
                <Form.Field error={!!(errors.bankAccountName)}>
                  <label htmlFor="bank" style={{ marginBottom: '0.5em', fontWeight: '600' }}>
                    Bank
                  </label>
                  <Form.Dropdown
                    placeholder="Bank"
                    fluid
                    selection
                    name="bankAccountName"
                    value={bankAccountName}
                    onChange={(e, { value }) => this.handleInputChange(e, { name: 'bankAccountName', value })}
                    options={bankList.data || []}
                    error={errors.bankAccountName && <div className="hlb-form-error-message">{errors.bankAccountName}</div>}
                  />
                </Form.Field>
                <Form.Field error={!!(errors.bankAccountNumber)}>
                  <label htmlFor="bankAccountNumber" style={{ marginBottom: '0.5em', fontWeight: '600' }}>
                    Bank account number
                  </label>
                  <Form.Input
                    name="bankAccountNumber"
                    placeholder="Bank account number"
                    value={bankAccountNumber}
                    onChange={this.handleInputChange}
                    error={errors.bankAccountNumber && <div className="hlb-form-error-message">{errors.bankAccountNumber}</div>}
                  />
                </Form.Field>
                <Form.Field error={!!(errors.agreed)}>
                  <Form.Checkbox
                    label={
                      <label htmlFor="agreeTerms" style={{ fontSize: '12px' }}>
                        I agree to the &nbsp;
                        <button
                          type="button"
                          className="hlb-campaign-link"
                          onClick={this.handleOnOpenTnc}
                          style={{ background: 'none', border: 'none', padding: 0 }}
                          aria-label="Open Terms and Conditions"
                        >
                          Terms and Conditions
                        </button>
                        , and consent to the processing of my personal details collected as stated above
                      </label>
                    }
                    checked={agreed}
                    name="agreeTerms"
                    onChange={(e, data) =>
                      this.setState({
                        agreed: data.checked,
                        errors: {
                          ...this.state.errors,
                          agreed: data.checked ? null : this.state.errors.agreed,
                        },
                      }, () => {
                        this.setState({ hasChanges: this.hasFormChanges() });
                      })
                    }
                    error={errors.agreed && <div className="hlb-form-error-message">{errors.agreed}</div>}
                  />
                </Form.Field>
              </Form>
            </Segment>
            <div style={{ display: 'flex', justifyContent: 'end', marginTop: '2em' }}>
              <Button
                type="submit"
                primary
                form="hlb-campaign-join-form"
                disabled={!agreed || isSubmitting || !hasChanges}
                loading={isSubmitting}
              >
                {editMode ? 'Update' : 'Confirm'}
              </Button>
            </div>
          </Modal.Description>
          <HLBCampaignTncModal
            open={this.state.openTncModal}
            onClose={this.handleOnCloseTnc}
          />
        </Modal.Content>
      </Modal>
    );
  }
}

export default connect(HLBCampaignJoinModal.mapStateToProps)(HLBCampaignJoinModal);



// WEBPACK FOOTER //
// ./src/components/HLBCampaign/HLBCampaignJoinModal.jsx